import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { AuthContext } from '../App';
import { useSubscription } from '../context/SubscriptionContext';
import { Star, BookOpen, Lock } from 'lucide-react';
import GameLimitIndicator from '../components/GameLimitIndicator';
import UpgradeModal from '../components/UpgradeModal';
import './Dashboard.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = React.useContext(AuthContext);
  const { 
    trackPlay, 
    fetchSubscriptionStatus,
    isPremium, 
    canPlayMore, 
    playedGamesCount, 
    maxPlayedGames 
  } = useSubscription();
  
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isProcessingPlay, setIsProcessingPlay] = useState(false);

  useEffect(() => {
    // Refresh subscription status on mount
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);
  
  // Handle clicking on a game card - track play BEFORE navigating
  const handleGameClick = async (e, game) => {
    e.preventDefault();
    
    // If user is premium, just navigate
    if (isPremium) {
      navigate(game.path);
      return;
    }
    
    // Check if already at limit before even trying
    if (!canPlayMore) {
      setShowUpgradeModal(true);
      return;
    }
    
    // Prevent double-clicks
    if (isProcessingPlay) return;
    setIsProcessingPlay(true);
    
    try {
      // Track the play with a unique game identifier
      const gameId = `dashboard-${game.path.replace(/\//g, '-')}`;
      const result = await trackPlay(gameId, game.path.replace(/\//g, '-'));
      
      if (!result.success && result.limitReached) {
        setShowUpgradeModal(true);
        setIsProcessingPlay(false);
        return;
      }
      
      // Play tracked successfully, now navigate
      navigate(game.path);
    } catch (error) {
      console.error('Failed to track play:', error);
      // Still navigate even if tracking fails
      navigate(game.path);
    } finally {
      setIsProcessingPlay(false);
    }
  };
  
  const games = [
    { title: t('quizzes'), path: '/quizzes', icon: <BookOpen size={40} />, color: '#FF6B9D' },
    { title: 'Learning Wheel', path: '/wheel-hub', icon: '🎡', color: '#667eea', isEmoji: true },
    { title: 'Puzzle Game', path: '/puzzle', icon: <Star size={40} />, color: '#FFE66D' },
    { title: 'Open the Box', path: '/open-box-hub', icon: '🎁', color: '#8B5CF6', isEmoji: true },
    { title: 'Flashcards', path: '/flashcard-hub', icon: '📚', color: '#87CEEB', isEmoji: true },
    { title: 'Anagram', path: '/anagram', icon: '🔤', color: '#DDA0DD', isEmoji: true },
    { title: 'Airplane Quiz Game', path: '/airplane-quiz', icon: '✈️', color: '#4A90E2', isEmoji: true },
    { title: 'Cheese Chase', path: '/game-hub', icon: '🧀', color: '#f59e0b', isEmoji: true },
    { title: 'Fish Quiz', path: '/fishing-quiz-hub', icon: '🎣', color: '#4CA1AF', isEmoji: true },
    { title: 'Quiz Blaster', path: '/quiz-blaster-hub', icon: '🎯', color: '#8B5CF6', isEmoji: true },
    { title: 'Save the Rabbit', path: '/save-the-rabbit-hub', icon: '🐰', color: '#22C55E', isEmoji: true },
    { title: 'Silly Gap Fill', path: '/silly-gap-fill-hub', icon: '🧩', color: '#F59E0B', isEmoji: true },
    { title: 'Funny Tables', path: '/kids-sorting-hub', icon: '⬜', color: '#EC4899', isEmoji: true },
    { title: 'Quiz Race', path: '/classroom-quiz-race-hub', icon: '🏁', color: '#3B82F6', isEmoji: true },
    { title: 'Word Order', path: '/word-order-hub', icon: '🔠', color: '#14B8A6', isEmoji: true },
    { title: 'Penalties Quiz', path: '/penalty-quiz-hub', icon: '⚽', color: '#22C55E', isEmoji: true },
    { title: 'Rocket Quiz', path: '/rocket-game-hub', icon: '🚀', color: '#FF6B35', isEmoji: true },
    { title: 'ماذا يوجد داخل الصندوق', path: '/whats-in-the-box-hub', icon: '🕵️', color: '#9B59B6', isEmoji: true, isNew: true }
  ];
  
  // Check if play buttons should be disabled
  const isPlayDisabled = !isPremium && !canPlayMore;
  
  return (
    <div className="dashboard-page" data-testid="dashboard">
      <div className="dashboard-container">
        <motion.div
          className="dashboard-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="welcome-section">
            {user?.picture && (
              <img src={user.picture} alt={user.name} className="user-avatar" data-testid="user-avatar" />
            )}
            <div>
              <h1 data-testid="welcome-message">Welcome, {user?.name}!</h1>
              <p className="user-role">{isPremium ? '⭐ Premium Member' : 'Free Plan'}</p>
            </div>
          </div>
        </motion.div>
        
        <div className="games-section">
          <div className="section-header">
            <h2>{t('activities')}</h2>
          </div>
          
          {/* Game Limit Indicator for Free Users - Shows both save and play limits */}
          <GameLimitIndicator showPlayLimit={true} />
          
          <div className="games-grid">
            {games.map((game, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * index }}
              >
                <div 
                  onClick={(e) => handleGameClick(e, game)}
                  className={`game-card ${isPlayDisabled ? 'disabled' : ''}`}
                  style={{ 
                    borderTop: `4px solid ${game.color}`,
                    cursor: isPlayDisabled ? 'not-allowed' : 'pointer',
                    opacity: isPlayDisabled ? 0.6 : 1
                  }}
                  data-testid={`game-card-${index}`}
                >
                  {game.isNew && <span className="new-badge">NEW</span>}
                  <div className="game-icon" style={game.isEmoji ? { fontSize: '3rem' } : { color: game.color }}>
                    {game.icon}
                  </div>
                  <h3>{game.title}</h3>
                  <div className={`play-badge ${isPlayDisabled ? 'disabled' : ''}`}>
                    {isPlayDisabled ? (
                      <>
                        <Lock size={14} style={{ marginRight: '4px' }} />
                        Limit Reached
                      </>
                    ) : (
                      '▶ Play Now'
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Upgrade Modal - Shows when play limit is reached */}
      <UpgradeModal 
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        limitType="play"
        currentCount={playedGamesCount}
        maxCount={maxPlayedGames || 3}
      />
    </div>
  );
}

export default Dashboard;
